import { HTMLAttributes, forwardRef, ReactNode } from 'react';
import './Alert.css';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'title'
> {
  /** Visual style variant */
  variant?: AlertVariant;
  /** Title of the alert */
  title?: ReactNode;
  /** Dismissible alert */
  dismissible?: boolean;
  /** Callback when dismiss is clicked */
  onDismiss?: () => void;
}

const AlertIcon = ({ variant }: { variant: AlertVariant }) => {
  const icons = {
    info: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-11a1 1 0 011 1v4a1 1 0 01-2 0V8a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z"
          fill="currentColor"
        />
      </svg>
    ),
    success: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.172 7.707 8.879a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          fill="currentColor"
        />
      </svg>
    ),
    warning: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8.257 3.099c.765-1.36 2.72-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM10 13a1 1 0 100-2 1 1 0 000 2zm-1-5a1 1 0 012 0v2a1 1 0 01-2 0V8z"
          fill="currentColor"
        />
      </svg>
    ),
    error: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          fill="currentColor"
        />
      </svg>
    ),
  };

  return <span className="ui-alert__icon">{icons[variant]}</span>;
};

/**
 * Alert component for displaying important messages.
 *
 * @example
 * ```tsx
 * <Alert variant="success" title="Success!">
 *   Your changes have been saved.
 * </Alert>
 * ```
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      variant = 'info',
      title,
      dismissible = false,
      onDismiss,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const classNames = ['ui-alert', `ui-alert--${variant}`, className]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} role="alert" className={classNames} {...props}>
        <AlertIcon variant={variant} />
        <div className="ui-alert__content">
          {title && <div className="ui-alert__title">{title}</div>}
          {children && <div className="ui-alert__message">{children}</div>}
        </div>
        {dismissible && (
          <button
            type="button"
            className="ui-alert__dismiss"
            onClick={onDismiss}
            aria-label="Dismiss alert"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
    );
  }
);

Alert.displayName = 'Alert';
